import posthog from "posthog-js";
import { posthogBeforeSend } from "./posthogBeforeSend";
import { sanitizeAnalyticsUrl } from "./sanitizeAnalyticsUrl";

const POSTHOG_KEY = import.meta.env?.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env?.VITE_POSTHOG_HOST as string | undefined) ??
  "https://us.i.posthog.com";
const DEFAULT_API_BASE_URL = "http://localhost:3000/v1";

export const TERREIRO_GROUP_TYPE = "terreiro";

/**
 * Hostnames the SDK should attach `X-POSTHOG-SESSION-ID` and
 * `X-POSTHOG-DISTINCT-ID` to. The SDK matches `URL.hostname` (no port).
 */
export const getPostHogTracingHostnames = (
  apiBaseUrl?: string,
): string[] => {
  const raw =
    apiBaseUrl?.trim() ||
    (import.meta.env?.VITE_API_BASE_URL as string | undefined)?.trim() ||
    DEFAULT_API_BASE_URL;

  try {
    const hostname = new URL(raw).hostname;
    return hostname ? [hostname] : [];
  } catch {
    return [];
  }
};

const isWebAnalyticsEnabled = (): boolean =>
  (import.meta.env?.VITE_POSTHOG_WEB_ANALYTICS_ENABLED as string | undefined) !==
  "false";

let initialized = false;

export const isPostHogConfigured = (): boolean => Boolean(POSTHOG_KEY);

export const getPostHogInitOptions = (): Parameters<typeof posthog.init>[1] => {
  const baseOptions: Parameters<typeof posthog.init>[1] = {
    api_host: POSTHOG_HOST,
    persistence: "localStorage+cookie",
    // posthog-js 1.359 reads `__add_tracing_headers` (docs name: tracing_headers).
    __add_tracing_headers: getPostHogTracingHostnames(),
    before_send: posthogBeforeSend,
    loaded: (ph) => {
      if (import.meta.env?.DEV) {
        console.info("[PostHog] Loaded — distinct_id:", ph.get_distinct_id());
      }
    },
  };

  if (!isWebAnalyticsEnabled()) {
    return {
      ...baseOptions,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
    };
  }

  return {
    ...baseOptions,
    // Recommended SPA defaults: history-based $pageview/$pageleave for web analytics.
    defaults: "2026-01-30",
    // Product analytics stays explicit; avoid noisy autocapture events.
    autocapture: false,
  };
};

/**
 * Initialise PostHog once. Safe to call multiple times — subsequent calls are
 * no-ops. When `VITE_POSTHOG_KEY` is absent the SDK is never loaded and all
 * capture calls silently no-op.
 */
export const initPostHog = (): void => {
  if (initialized) return;

  if (!POSTHOG_KEY) {
    if (import.meta.env?.DEV) {
      console.info("[PostHog] VITE_POSTHOG_KEY not set — analytics disabled.");
    }
    initialized = true;
    return;
  }

  posthog.init(POSTHOG_KEY, getPostHogInitOptions());

  initialized = true;
};

/**
 * Returns whether a feature flag is enabled. When PostHog is not configured,
 * returns the provided default.
 */
export const isFeatureFlagEnabled = (
  flagKey: string,
  defaultValue = false,
): boolean => {
  if (!POSTHOG_KEY) {
    return defaultValue;
  }

  return posthog.isFeatureEnabled(flagKey) ?? defaultValue;
};

/**
 * Subscribe to PostHog feature flag updates. Returns an unsubscribe function.
 */
export const onFeatureFlags = (callback: () => void): (() => void) => {
  if (!POSTHOG_KEY) {
    return () => undefined;
  }

  return posthog.onFeatureFlags(callback);
};

/**
 * Thin wrapper around `posthog.capture()`.
 * If PostHog was not initialised (missing key), the call is a silent no-op.
 */
export const capture = (
  event: string,
  properties?: Record<string, unknown>,
): void => {
  if (!POSTHOG_KEY) return;
  posthog.capture(event, properties);
};

/**
 * Thin wrapper around `posthog.identify()`.
 * If PostHog was not initialised (missing key), the call is a silent no-op.
 */
export const identify = (
  userId: string,
  properties?: Record<string, unknown>,
  unset?: string[],
): void => {
  if (!POSTHOG_KEY || !userId.trim()) return;
  posthog.identify(userId, properties);
  if (unset && unset.length > 0) {
    posthog.capture("$set", { $unset: unset });
  }
};

export const syncTerreiroGroup = (terreiroId?: string | null): void => {
  if (!POSTHOG_KEY) return;
  if (terreiroId) {
    posthog.group(TERREIRO_GROUP_TYPE, terreiroId);
    return;
  }
  posthog.resetGroups();
};

export const reloadFeatureFlags = (): void => {
  if (!POSTHOG_KEY) return;
  posthog.reloadFeatureFlags();
};

export const reset = (): void => {
  if (!POSTHOG_KEY) return;
  posthog.resetGroups();
  posthog.reset();
};

export { posthog, sanitizeAnalyticsUrl };
